import { useState } from 'react'
import { Link } from '@inertiajs/react'
import SeoHead from '../../components/SeoHead'
import MarketingFooter from '../../components/MarketingFooter'

const SIGNUP_HREF = '/users/sign_up?plan=solo&src=for-private-lenders'

const PAIN_CARDS = [
  {
    title: 'Spreadsheet sprawl.',
    body:
      "One tab per loan, payment dates handwritten, amortization formulas you can't quite remember why you wrote.",
  },
  {
    title: 'Borrower confusion.',
    body:
      '"Wait, how much is left on my loan?" gets asked monthly. You go hunt the spreadsheet.',
  },
  {
    title: 'Tax season panic.',
    body:
      'January 31st rolls around. Every borrower needs a 1098. You have a weekend.',
  },
]

const FEATURES = [
  {
    title: 'One dashboard for every loan.',
    body: 'Outstanding principal, next payment, days delinquent, IRR — live. No formulas.',
  },
  {
    title: 'Borrower portal that does the explaining.',
    body:
      'Borrowers log in, see their schedule, pay, download their statement. You stop being customer support.',
  },
  {
    title: '1098s in two clicks.',
    body: 'Bulk-generate every borrower 1098 for the year. Done in 30 seconds, not a weekend.',
  },
]

const FAQS = [
  {
    q: 'Who is this for?',
    a: 'Solo private lenders and small lending operations — people managing $50K to $500K across 1 to 25 loans. If you run fix-and-flip bridge loans, long-term holds, or any kind of private lending and currently live in a spreadsheet, this was built for you.',
  },
  {
    q: 'Do you handle ACH?',
    a: "No — we don't move money. You collect payments through your bank, Zelle, wire, or however you already do it. LendSolo tracks the records: payment dates, principal/interest splits, 1098 totals, and borrower statements.",
  },
  {
    q: 'Can I import my spreadsheet?',
    a: 'Yes. Upload a CSV or XLSX file and LendSolo auto-detects your columns — borrower name, principal, rate, term. Review the mapping, confirm, and your loans are imported in minutes.',
  },
  {
    q: 'Is this legal advice?',
    a: 'No. LendSolo is a tracking and reporting platform, not a law firm, regulated custodian, or securities advisor. Your state lending law, usury limits, and regulatory compliance remain yours.',
  },
  {
    q: 'What happens after my trial?',
    a: 'Your 14-day trial requires no credit card. After it ends, you choose a plan — Solo ($29/mo), Pro ($49/mo), or Fund ($99/mo). If you do nothing, your account pauses; your data stays exportable.',
  },
]

const SCHEMA = {
  '@graph': [
    {
      '@type': 'FinancialProduct',
      name: 'LendSolo',
      description:
        'Loan management software for private lenders. Track loans, automate payments and 1098s, generate AI deal memos.',
      url: 'https://lendsolo.com/for-private-lenders',
      feesAndCommissionsSpecification: 'Solo $29/mo · Pro $49/mo · Fund $99/mo',
      provider: {
        '@type': 'Organization',
        name: 'LendSolo',
        url: 'https://lendsolo.com',
      },
    },
    {
      '@type': 'Organization',
      name: 'LendSolo',
      url: 'https://lendsolo.com',
      description:
        'LendSolo builds loan management software for private lenders — founded by a practicing private lender.',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: 'support@lendsolo.com',
      },
    },
  ],
}

export default function ForPrivateLenders() {
  return (
    <>
      <SeoHead
        title="Loan Management Software for Private Lenders — LendSolo"
        description="Built by a practicing private lender. Track loans, automate payments and 1098s, generate AI deal memos. Solo $29/mo. Try free."
        canonicalPath="/for-private-lenders"
        schema={SCHEMA}
      />

      <div className="min-h-screen" style={{ backgroundColor: '#f8f7f4' }}>
        <Header />
        <Hero />
        <ProductWalkthroughAnchor />
        <PainSection />
        <FeatureTrio />
        <FounderVoice />
        <PricingStrip />
        <FaqSection />
        <FinalCta />
        <MarketingFooter />
      </div>
    </>
  )
}

function Header() {
  return (
    <header className="bg-white/80 backdrop-blur-sm border-b border-[#e2e0d8] sticky top-0 z-40">
      <div className="max-w-[1140px] mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: '#0f1a2e' }}
          >
            <span className="text-[#34d399] font-bold text-sm font-mono">LS</span>
          </div>
          <span className="text-lg font-bold text-[#0f1a2e] font-display">LendSolo</span>
        </Link>
        <nav className="flex items-center gap-2 sm:gap-3">
          <Link
            href="/"
            className="hidden sm:inline-block text-sm text-[#5a6578] hover:text-[#0f1a2e] px-3 py-2"
          >
            Product
          </Link>
          <Link
            href="/blog"
            className="hidden sm:inline-block text-sm text-[#5a6578] hover:text-[#0f1a2e] px-3 py-2"
          >
            Blog
          </Link>
          <Link
            href="/users/sign_in"
            className="text-sm text-[#5a6578] hover:text-[#0f1a2e] px-3 py-2"
          >
            Sign In
          </Link>
          <Link
            href={SIGNUP_HREF}
            className="text-sm font-semibold text-white bg-[#0f1a2e] hover:-translate-y-0.5 hover:shadow-lg transition-all px-4 py-2 rounded-lg"
          >
            Get Started
          </Link>
        </nav>
      </div>
    </header>
  )
}

function Hero() {
  return (
    <section className="relative overflow-hidden pt-20 sm:pt-28 pb-20 sm:pb-24">
      <div
        className="absolute top-[-40%] left-1/2 -translate-x-1/2 w-[800px] h-[800px] pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(26,122,76,0.06) 0%, transparent 70%)',
        }}
      />
      <div className="relative max-w-[1140px] mx-auto px-6 sm:px-8 text-center">
        <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-5">
          <span className="w-2 h-2 rounded-full bg-[#22c55e] shadow-[0_0_8px_rgba(34,197,94,0.5)]" />
          For private lenders
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-[56px] leading-[1.1] text-[#0f1a2e] max-w-[780px] mx-auto tracking-tight">
          Built by a private lender, <em className="italic text-[#1a7a4c]">not a software company</em>.
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-[#5a6578] max-w-[620px] mx-auto">
          LendSolo is the loan management platform I wished existed when I started lending. Track every loan, every payment, every borrower — without the spreadsheet sprawl.
        </p>
        <div className="mt-10 flex gap-3 justify-center flex-wrap">
          <Link
            href={SIGNUP_HREF}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#0f1a2e] text-white rounded-[10px] text-[15px] font-semibold hover:-translate-y-0.5 hover:shadow-lg transition-all"
          >
            Start your free trial
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M3 8h10m0 0L9 4m4 4L9 12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
          <a
            href="#product-walkthrough"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-transparent text-[#0f1a2e] rounded-[10px] text-[15px] font-semibold border-[1.5px] border-[#e2e0d8] hover:border-[#5a6578] hover:bg-white transition-all"
          >
            See it in 90 seconds
          </a>
        </div>
      </div>
    </section>
  )
}

function ProductWalkthroughAnchor() {
  return <div id="product-walkthrough" aria-hidden="true" />
}

function PainSection() {
  return (
    <section className="py-20 sm:py-24 bg-white border-y border-[#e2e0d8]">
      <div className="max-w-[1140px] mx-auto px-6 sm:px-8">
        <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
          The problem
        </p>
        <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight max-w-[780px] mb-12">
          You're a lender. Why are you doing accountant work?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {PAIN_CARDS.map((card) => (
            <div
              key={card.title}
              className="bg-[#f8f7f4] border border-[#e2e0d8] rounded-[14px] p-7 hover:border-[#c5c3ba] transition-colors"
            >
              <h3 className="font-display text-xl font-bold text-[#0f1a2e] mb-3">
                {card.title}
              </h3>
              <p className="text-[15px] leading-relaxed text-[#5a6578]">{card.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function FeatureTrio() {
  return (
    <section className="py-20 sm:py-24" style={{ backgroundColor: '#f8f7f4' }}>
      <div className="max-w-[1140px] mx-auto px-6 sm:px-8">
        <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
          What replaces it
        </p>
        <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight max-w-[780px] mb-12">
          Your portfolio, in three views.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <div
              key={f.title}
              className="bg-white border border-[#e2e0d8] rounded-[14px] p-7 shadow-[0_2px_12px_rgba(0,0,0,0.04)]"
            >
              <div className="font-mono text-xs text-[#1a7a4c] mb-3">0{i + 1}</div>
              <h3 className="font-display text-xl font-bold text-[#0f1a2e] mb-3">
                {f.title}
              </h3>
              <p className="text-[15px] leading-relaxed text-[#5a6578]">{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function FounderVoice() {
  return (
    <section className="py-20 sm:py-24 bg-white border-y border-[#e2e0d8]">
      <div className="max-w-[820px] mx-auto px-6 sm:px-8">
        <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
          Founder
        </p>
        <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight mb-6">
          "I lend in Illinois. I built this for me first."
        </h2>
        <p className="text-[17px] leading-relaxed text-[#5a6578] mb-4">
          LendSolo isn't a product team's idea of what lending software should be. It's the tool I use every week to manage my own portfolio. Every feature exists because I needed it.
        </p>
        <p className="text-sm font-semibold text-[#0f1a2e]">— Pete, founder</p>
      </div>
    </section>
  )
}

function PricingStrip() {
  const tiers = [
    { name: 'Solo', price: '$29', note: 'Up to 5 loans' },
    { name: 'Pro', price: '$49', note: 'Up to 25 loans' },
    { name: 'Fund', price: '$99', note: 'Unlimited + investors' },
  ]
  return (
    <section className="py-16 sm:py-20" style={{ backgroundColor: '#f8f7f4' }}>
      <div className="max-w-[1140px] mx-auto px-6 sm:px-8">
        <div className="bg-white border border-[#e2e0d8] rounded-[14px] p-6 sm:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-2">
              Pricing
            </p>
            <p className="font-display text-xl sm:text-2xl text-[#0f1a2e]">
              Simple monthly pricing.{' '}
              <span className="text-[#5a6578] font-normal">Annual = 2 months free.</span>
            </p>
          </div>
          <div className="flex gap-3 sm:gap-5">
            {tiers.map((t) => (
              <div key={t.name} className="flex flex-col">
                <span className="text-xs font-mono uppercase tracking-wider text-[#8c95a6]">
                  {t.name}
                </span>
                <span className="font-display text-2xl text-[#0f1a2e]">
                  {t.price}
                  <span className="text-sm text-[#5a6578]">/mo</span>
                </span>
                <span className="text-xs text-[#8c95a6]">{t.note}</span>
              </div>
            ))}
          </div>
          <Link
            href="/#pricing"
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-[#0f1a2e] border-[1.5px] border-[#e2e0d8] rounded-lg hover:border-[#5a6578] transition-all"
          >
            Full pricing →
          </Link>
        </div>
      </div>
    </section>
  )
}

function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  return (
    <section className="py-20 sm:py-24 bg-white border-y border-[#e2e0d8]">
      <div className="max-w-[720px] mx-auto px-6 sm:px-8">
        <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
          FAQ
        </p>
        <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight mb-10">
          Common questions
        </h2>
        <div className="divide-y divide-[#e2e0d8]">
          {FAQS.map((item, i) => {
            const isOpen = openIndex === i
            return (
              <div key={item.q} className="py-6">
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between text-left"
                >
                  <span className="text-base font-semibold text-[#0f1a2e] pr-4">
                    {item.q}
                  </span>
                  <span className="text-xl font-light text-[#8c95a6] min-w-[24px] text-center">
                    {isOpen ? '−' : '+'}
                  </span>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isOpen ? 'max-h-[400px] pt-4 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <p className="text-[15px] leading-relaxed text-[#5a6578]">{item.a}</p>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

function FinalCta() {
  return (
    <section className="py-20 sm:py-24" style={{ backgroundColor: '#f8f7f4' }}>
      <div className="max-w-[1140px] mx-auto px-6 sm:px-8 text-center">
        <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
          Ready?
        </p>
        <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight max-w-[640px] mx-auto mb-4">
          Stop patching spreadsheets. Start running a portfolio.
        </h2>
        <p className="text-[17px] leading-relaxed text-[#5a6578] max-w-[600px] mx-auto mb-9">
          14-day free trial. No credit card. Import your spreadsheet in under two minutes.
        </p>
        <Link
          href={SIGNUP_HREF}
          className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#0f1a2e] text-white rounded-[10px] text-[15px] font-semibold hover:-translate-y-0.5 hover:shadow-lg transition-all"
        >
          Start your free trial
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M3 8h10m0 0L9 4m4 4L9 12"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Link>
      </div>
    </section>
  )
}
